import { useState, useEffect, useCallback } from "react";
import { cn } from "../../lib/utils.js";

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: "communication" | "documents" | "cre";
  status: "available" | "coming_soon";
}

interface CredentialField {
  key: string;
  label: string;
  placeholder: string;
}

// Per-plugin credential fields — OAuth plugins need client_id + client_secret,
// API-key plugins just need api_key.
const PLUGIN_CREDENTIAL_FIELDS: Record<string, CredentialField[]> = {
  airtable: [{ key: "api_key", label: "Personal Access Token", placeholder: "pat..." }],
  notion: [{ key: "api_key", label: "Internal Integration Secret", placeholder: "secret_..." }],
};

const OAUTH_FIELDS: CredentialField[] = [
  { key: "client_id", label: "OAuth Client ID", placeholder: "Enter your OAuth client ID" },
  { key: "client_secret", label: "OAuth Client Secret", placeholder: "Enter your OAuth client secret" },
];

function credentialFields(pluginId: string): CredentialField[] {
  return PLUGIN_CREDENTIAL_FIELDS[pluginId] ?? OAUTH_FIELDS;
}

const INTEGRATIONS: Integration[] = [
  // Communication
  { id: "gmail", name: "Gmail", description: "Legal communications, COI delivery, intake email for compliance docs.", icon: "📧", category: "communication", status: "available" },
  { id: "slack", name: "Slack", description: "Team notifications, approval workflows, and AI summaries in channels.", icon: "💬", category: "communication", status: "available" },
  // Documents
  { id: "googledrive", name: "Google Drive", description: "Contract and document storage. Import drafts directly into review workflows.", icon: "📂", category: "documents", status: "available" },
  { id: "googlesheets", name: "Google Sheets", description: "Portfolio tracking, rent roll exports, and COI status sheets.", icon: "📊", category: "documents", status: "available" },
  { id: "notion", name: "Notion", description: "Matter notes, deal memos, and internal knowledge base sync.", icon: "📓", category: "documents", status: "available" },
  { id: "googlecalendar", name: "Google Calendar", description: "Lease expiration, renewal deadlines, and COI expiry reminders.", icon: "📅", category: "documents", status: "available" },
  // CRE & PM
  { id: "airtable", name: "Airtable", description: "Contract portfolio tracker and lease database with AI-generated fields.", icon: "🗃️", category: "cre", status: "available" },
  { id: "hubspot", name: "HubSpot", description: "Tenant and deal CRM. Sync lease abstracts and contact records.", icon: "🔗", category: "cre", status: "available" },
  // Coming soon
  { id: "docusign", name: "DocuSign", description: "Import drafts and push executed agreements directly from the workflow.", icon: "✍️", category: "documents", status: "coming_soon" },
  { id: "onedrive", name: "OneDrive / SharePoint", description: "Microsoft document storage for legal files and executed contracts.", icon: "☁️", category: "documents", status: "coming_soon" },
  { id: "jira", name: "Jira", description: "Link AI tasks to tickets. Track legal review cycles in your existing system.", icon: "📋", category: "communication", status: "coming_soon" },
  { id: "yardi", name: "Yardi", description: "Push lease abstracts directly to your property management system.", icon: "🏢", category: "cre", status: "coming_soon" },
];

const CATEGORY_LABELS: Record<string, string> = {
  communication: "Communication",
  documents: "Documents & Storage",
  cre: "CRE & Property Management",
};

const ENGINE = "http://localhost:3001";

export function IntegrationsPanel() {
  const [connecting, setConnecting] = useState<string | null>(null);
  const [connectedSet, setConnectedSet] = useState<Set<string>>(new Set());
  const [setupInstructions, setSetupInstructions] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Credential modal state
  const [credentialModal, setCredentialModal] = useState<Integration | null>(null);
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});

  // On mount, call setupCorsair to see which plugins are already configured
  const refreshSetup = useCallback(async () => {
    try {
      const res = await fetch(`${ENGINE}/integrations/setup`);
      const data = await res.json() as { instructions: string; oauthUrls: string[]; error?: string };
      if (data.error) {
        setError(data.error);
        return;
      }
      setSetupInstructions(data.instructions);
      // Detect which plugins have auth configured from the instructions text
      const text = data.instructions.toLowerCase();
      const nowConnected = new Set<string>();
      for (const integration of INTEGRATIONS) {
        // Corsair reports "✓ authenticated" or "auth configured" for connected plugins
        if (text.includes(integration.id) && (text.includes("authenticated") || text.includes("configured") || text.includes("✓"))) {
          nowConnected.add(integration.id);
        }
      }
      setConnectedSet(nowConnected);
    } catch {
      // Engine not running or endpoint doesn't exist yet — silently ignore
    }
  }, []);

  useEffect(() => { void refreshSetup(); }, [refreshSetup]);

  // Open the credential input modal for a plugin
  const handleConnect = (integration: Integration) => {
    if (integration.status === "coming_soon") return;
    if (connectedSet.has(integration.id)) return;
    // Reset credential fields for this plugin
    const emptyValues = credentialFields(integration.id).reduce<Record<string, string>>(
      (acc, f) => ({ ...acc, [f.key]: "" }),
      {},
    );
    setCredentialValues(emptyValues);
    setCredentialModal(integration);
    setError(null);
  };

  // Submit credentials → store via Corsair → open OAuth browser flow
  const handleCredentialSubmit = async () => {
    if (!credentialModal) return;
    const integration = credentialModal;
    setCredentialModal(null);
    setConnecting(integration.id);
    setError(null);

    try {
      const res = await fetch(`${ENGINE}/integrations/credentials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plugin: integration.id, credentials: credentialValues }),
      });
      const data = await res.json() as { instructions: string; oauthUrls: string[]; error?: string };

      if (data.error) {
        setError(data.error);
        setConnecting(null);
        return;
      }

      // Try to find the OAuth authorization URL for this plugin
      const relevantUrl = data.oauthUrls.find((u) =>
        u.toLowerCase().includes(integration.id.replace(/^google/, "google"))
      ) ?? data.oauthUrls[0];

      if (relevantUrl) {
        await fetch(`${ENGINE}/open?path=${encodeURIComponent(relevantUrl)}`).catch(() => {
          window.open(relevantUrl, "_blank");
        });
        setSetupInstructions(
          `Opened ${integration.name} sign-in in your browser. Complete the flow and return here.`,
        );
      } else {
        setSetupInstructions(data.instructions);
      }
    } catch {
      setError("Could not reach the engine. Make sure pnpm dev:engine is running.");
    }

    setConnecting(null);
    setTimeout(() => { void refreshSetup(); }, 5000);
  };

  const byCategory = INTEGRATIONS.reduce<Record<string, Integration[]>>((acc, i) => {
    if (!acc[i.category]) acc[i.category] = [];
    acc[i.category]!.push(i);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[var(--border)]">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Integrations</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-0.5">
          Connect your tools. Claude can access them during any conversation.
        </p>
      </div>

      {/* How it works */}
      <div className="mx-6 mt-4 p-4 rounded-xl text-sm" style={{ background: "var(--accent-muted)", border: "1px solid var(--accent-border)" }}>
        <span className="text-[var(--text-primary)] font-medium">How it works: </span>
        <span className="text-[var(--text-secondary)]">
          Clicking Connect opens the service's OAuth sign-in in your browser. Once you approve, tokens are saved locally and Claude can use that tool in any conversation. Try:{" "}
          <em>"Check my Gmail for any COI emails from last week."</em>
        </span>
      </div>

      {/* Corsair instructions / status */}
      {setupInstructions && (
        <div className="mx-6 mt-3 p-4 rounded-xl text-xs font-mono whitespace-pre-wrap max-h-32 overflow-y-auto"
          style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
          {setupInstructions}
        </div>
      )}

      {error && (
        <div className="mx-6 mt-3 p-3 rounded-xl text-sm text-red-400" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
          {error}
        </div>
      )}

      {/* Integration lists */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-8">
        {Object.entries(byCategory).map(([category, items]) => (
          <div key={category}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3">
              {CATEGORY_LABELS[category]}
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {items.map((integration) => {
                const isConnected = connectedSet.has(integration.id);
                const isConnecting = connecting === integration.id;
                const isComingSoon = integration.status === "coming_soon";

                return (
                  <div
                    key={integration.id}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-xl transition-all",
                      isComingSoon ? "opacity-50" : "hover:bg-[var(--bg-tertiary)]",
                    )}
                    style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: "var(--bg-tertiary)" }}>
                      {integration.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-primary)]">{integration.name}</span>
                        {isComingSoon && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: "var(--text-secondary)" }}>
                            Coming soon
                          </span>
                        )}
                        {isConnected && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(52,211,153,0.12)", color: "#34D399", border: "1px solid rgba(52,211,153,0.25)" }}>
                            Connected
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5 truncate">{integration.description}</p>
                    </div>

                    {!isComingSoon && (
                      <button
                        onClick={() => handleConnect(integration)}
                        disabled={isConnected || isConnecting}
                        className={cn(
                          "flex-shrink-0 text-xs font-medium px-4 py-2 rounded-lg transition-all",
                          isConnected || isConnecting ? "opacity-50 cursor-default" : "hover:opacity-90",
                        )}
                        style={
                          isConnected
                            ? { background: "rgba(52,211,153,0.1)", color: "#34D399" }
                            : { background: "var(--accent)", color: "white" }
                        }
                      >
                        {isConnected ? "Connected" : isConnecting ? "Opening…" : "Connect"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Credential input modal */}
      {credentialModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => setCredentialModal(null)}
        >
          <div
            className="w-full max-w-sm mx-4 rounded-2xl p-6 flex flex-col gap-4"
            style={{ background: "var(--bg-primary)", border: "1px solid var(--border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{credentialModal.icon}</span>
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                  Connect {credentialModal.name}
                </h3>
                <p className="text-xs text-[var(--text-secondary)]">
                  Enter your OAuth app credentials to enable this integration.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {credentialFields(credentialModal.id).map((field) => (
                <div key={field.key} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-[var(--text-secondary)]">
                    {field.label}
                  </label>
                  <input
                    type="password"
                    value={credentialValues[field.key] ?? ""}
                    onChange={(e) =>
                      setCredentialValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                    }
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent)]"
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setCredentialModal(null)}
                className="px-4 py-2 text-xs rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleCredentialSubmit()}
                disabled={credentialFields(credentialModal.id).some((f) => !credentialValues[f.key]?.trim())}
                className="px-4 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-40"
                style={{ background: "var(--accent)", color: "white" }}
              >
                Save & Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
